import {
  DEFAULT_HORIZON_DAYS,
  WEEKDAY_NAMES,
  defaultCaption,
  describePlan,
  planPosts,
  sanitiseRules,
  type PostRules,
} from "@rp/shared";
import { api, apiUrl, tokens, type Clip } from "../lib/api.js";
import { POSTLY_URL } from "../lib/publish.js";

/**
 * Deciding when the clips go out.
 *
 * The clips are finished at the end of the previous screen. This is the part
 * that gets them seen, and it is the part a streamer does worst: twenty clips
 * posted in one afternoon reach fewer people than the same twenty over a
 * fortnight, and nobody opens a scheduler twenty times.
 *
 * So the shape is set once — these clips, two a day, Tuesday to Saturday, six
 * and nine in the evening — and the result leaves as a CSV Postly bulk
 * schedules from. Nothing here publishes anything: Stream-Cut holds no platform
 * OAuth and cannot see how a post did, which is exactly why the hand-off
 * exists.
 *
 * The preview is computed with the same `planPosts` the server commits with, so
 * what is shown and what is saved cannot disagree. The server still recomputes
 * rather than trusting these slots — one answer to one question — and the order
 * of the clips is the streamer's, which is an input rather than a computation.
 */

const esc = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

/** Today, as the local wall-clock date — which is what a slot is. */
function todayLocal(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

interface Saved {
  rules: PostRules | null;
  max_horizon_days: number;
  scheduled: { clip_id: string; post_date: string; post_time: string; caption: string | null }[];
}

export async function scheduleView(root: HTMLElement) {
  root.innerHTML = `<header class="page-head"><h1>Schedule</h1></header>
    <div id="schBody" class="loading">Loading…</div>`;
  const body = root.querySelector<HTMLElement>("#schBody")!;

  let saved: Saved;
  let clips: Clip[];
  try {
    [saved, clips] = await Promise.all([
      api<Saved>("GET", "/v1/schedule"),
      api<{ clips: Clip[] }>("GET", "/v1/clips?status=ready&sort=audience&limit=200").then(
        (r) => r.clips,
      ),
    ]);
  } catch (err) {
    body.classList.remove("loading");
    body.textContent = err instanceof Error ? err.message : String(err);
    return;
  }
  body.classList.remove("loading");

  // Ranked by what the crowd did, because the first post gets the most reach
  // and the order here is the order they go out in. The streamer can untick
  // anything; they cannot reorder, which would be a drag-and-drop list for a
  // decision the crowd already made better.
  let rules: PostRules = saved.rules ?? {
    weekdays: [],
    times: ["18:00"],
    startDate: todayLocal(),
    horizonDays: DEFAULT_HORIZON_DAYS,
  };

  // Pre-ticked from whatever is already scheduled, or everything on a first
  // visit: somebody arriving here wants to post their clips, and starting with
  // nothing selected makes them tick twenty boxes to say so.
  const already = new Set(saved.scheduled.map((s) => s.clip_id));
  let chosen = new Set(already.size ? already : clips.map((c) => c.id));

  const captionOf = (clip: Clip) =>
    saved.scheduled.find((s) => s.clip_id === clip.id)?.caption ??
    defaultCaption({
      title: clip.title,
      gameName: clip.game_name,
      videoTitle: clip.video_title,
      audienceScore: clip.audience_score,
    });

  function render() {
    const picked = clips.filter((c) => chosen.has(c.id));
    const plan = planPosts(
      picked.map((c) => c.id),
      rules,
    );
    const r = sanitiseRules(rules);
    const slotOf = new Map(plan.slots.map((s) => [s.clipId, s]));

    body.innerHTML = `
      <section class="card">
        <h2>When</h2>
        <div class="sch-rules">
          <label>First post
            <input type="date" id="schStart" value="${esc(rules.startDate)}">
          </label>
          <label>Stop after
            <select id="schHorizon">
              ${[7, 14, 21, 30, 60]
                .filter((d) => d <= saved.max_horizon_days)
                .map(
                  (d) =>
                    `<option value="${d}"${
                      (rules.horizonDays ?? DEFAULT_HORIZON_DAYS) === d ? " selected" : ""
                    }>${d} days</option>`,
                )
                .join("")}
            </select>
          </label>
        </div>

        <p class="meta">On these days — none chosen means every day</p>
        <div class="sch-days">
          ${WEEKDAY_NAMES.map(
            (name, i) =>
              `<label class="sch-day${r.weekdays.includes(i) ? " on" : ""}">
                 <input type="checkbox" data-weekday="${i}"${
                   r.weekdays.includes(i) ? " checked" : ""
                 }>${esc(name.slice(0, 3))}
               </label>`,
          ).join("")}
        </div>

        <p class="meta">At these times — one post per time, per day</p>
        <div class="sch-times" id="schTimes">
          ${r.times
            .map(
              (t) => `<span class="sch-time">
                 <input type="time" data-time value="${esc(t)}">
                 <button type="button" class="link" data-drop-time="${esc(t)}">Remove</button>
               </span>`,
            )
            .join("")}
          <button type="button" class="btn small" id="schAddTime">Add a time</button>
        </div>

        <p class="meta" id="schSummary">${esc(describePlan(plan, rules))}</p>
      </section>

      <section class="card">
        <h2>What${picked.length ? ` · ${picked.length} of ${clips.length}` : ""}</h2>
        <p class="meta">Best first, by how hard your crowd reacted — the first post
           gets the most reach. Untick anything you would rather keep back.</p>
        ${
          clips.length === 0
            ? `<p class="meta">No finished clips yet.</p>`
            : `<div class="sch-list">${clips
                .map((clip) => {
                  const slot = slotOf.get(clip.id);
                  const out = chosen.has(clip.id) && !slot;
                  return `<div class="sch-row${chosen.has(clip.id) ? " on" : ""}" data-id="${esc(
                    clip.id,
                  )}">
                    <label class="sch-pick">
                      <input type="checkbox" data-pick${chosen.has(clip.id) ? " checked" : ""}>
                    </label>
                    <span class="sch-when">${
                      slot
                        ? `${esc(slot.date)} · ${esc(slot.time)}`
                        : out
                          ? `<span class="sch-over">no room</span>`
                          : "—"
                    }</span>
                    <input class="sch-caption" data-caption maxlength="2000"
                           value="${esc(captionOf(clip))}"
                           placeholder="What the post says">
                    ${
                      clip.audience_score > 0
                        ? `<span class="meta">🔥 ${clip.audience_score}</span>`
                        : ""
                    }
                  </div>`;
                })
                .join("")}</div>`
        }
      </section>

      <section class="card">
        <h2>Hand it to Postly</h2>
        <p class="meta">Save the schedule, download the file, then import it in Postly
           under Bulk Post. Every row carries the clip, what the post says and when it
           goes out, so nothing has to be typed twice.</p>
        <p class="meta"><strong>The times are as written</strong> — 18:00 means 18:00 in
           whichever timezone your Postly workspace is set to. Set that once there and
           these line up.</p>
        <div class="actions">
          <button class="btn" id="schSave">Save the schedule</button>
          <button class="btn" id="schCsv"${plan.slots.length ? "" : " disabled"}>Download the CSV</button>
          <a class="btn btn-outline" href="${POSTLY_URL}" target="_blank"
             rel="noopener">Open Postly</a>
        </div>
        <p class="meta" id="schNote"></p>
      </section>`;
  }

  const note = (text: string) => {
    const el = body.querySelector<HTMLElement>("#schNote");
    if (el) el.textContent = text;
  };

  // Delegated: every control is re-rendered on each change, and handlers bound
  // to replaced nodes stop working silently.
  body.addEventListener("change", (e) => {
    const el = e.target as HTMLInputElement;

    if (el.id === "schStart") {
      rules = { ...rules, startDate: el.value };
      render();
      return;
    }
    if (el.id === "schHorizon") {
      rules = { ...rules, horizonDays: Number(el.value) };
      render();
      return;
    }
    if (el.dataset.weekday !== undefined) {
      const day = Number(el.dataset.weekday);
      const next = new Set(sanitiseRules(rules).weekdays);
      if (el.checked) next.add(day);
      else next.delete(day);
      rules = { ...rules, weekdays: [...next] };
      render();
      return;
    }
    if (el.dataset.time !== undefined) {
      // Rebuilt from every field rather than patched by index: a time edited to
      // match another would otherwise leave a duplicate that sanitiseRules
      // silently drops, and the row count would stop matching the posts a day.
      rules = {
        ...rules,
        times: [...body.querySelectorAll<HTMLInputElement>("[data-time]")].map((i) => i.value),
      };
      render();
      return;
    }
    if (el.hasAttribute("data-pick")) {
      const id = el.closest<HTMLElement>("[data-id]")?.dataset.id;
      if (!id) return;
      if (el.checked) chosen.add(id);
      else chosen.delete(id);
      render();
    }
  });

  body.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    if (target.id === "schAddTime") {
      const times = sanitiseRules(rules).times;
      // Three hours after the last, which is the gap somebody adding a second
      // slot almost always wants, and trivially editable if not.
      const last = times[times.length - 1] ?? "18:00";
      const [h, m] = last.split(":").map(Number);
      const next = `${String((h! + 3) % 24).padStart(2, "0")}:${String(m!).padStart(2, "0")}`;
      rules = { ...rules, times: [...times, next] };
      render();
      return;
    }

    const dropped = target.closest<HTMLElement>("[data-drop-time]")?.dataset.dropTime;
    if (dropped !== undefined) {
      rules = {
        ...rules,
        times: sanitiseRules(rules).times.filter((t) => t !== dropped),
      };
      render();
      return;
    }

    if (target.id === "schSave") {
      const captions: Record<string, string> = {};
      body.querySelectorAll<HTMLElement>("[data-id]").forEach((row) => {
        const id = row.dataset.id!;
        const field = row.querySelector<HTMLInputElement>("[data-caption]");
        if (field) captions[id] = field.value;
      });

      (target as HTMLButtonElement).disabled = true;
      note("Saving…");
      try {
        const res = await api<{
          slots: unknown[];
          unplaced: string[];
          skipped: string[];
          last_date: string | null;
        }>("PUT", "/v1/schedule", {
          clip_ids: clips.filter((c) => chosen.has(c.id)).map((c) => c.id),
          rules,
          captions,
        });
        saved = await api<Saved>("GET", "/v1/schedule");
        note(
          `Saved. ${res.slots.length} post${res.slots.length === 1 ? "" : "s"}` +
            (res.last_date ? ` through ${res.last_date}` : "") +
            (res.unplaced.length ? `, ${res.unplaced.length} with no room` : "") +
            // Named separately from "no room": a clip that was deleted or is
            // still rendering is a different problem from one that did not fit.
            (res.skipped.length
              ? `, ${res.skipped.length} skipped because they are not ready`
              : "") +
            ".",
        );
      } catch (err) {
        note(err instanceof Error ? err.message : String(err));
      } finally {
        (target as HTMLButtonElement).disabled = false;
      }
      return;
    }

    if (target.id === "schCsv") {
      note("Building the file…");
      try {
        // Fetched with the token and handed over as a download rather than
        // navigated to: the endpoint needs the Authorization header, which a
        // plain link cannot carry.
        const res = await fetch(apiUrl("/v1/schedule/postly.csv"), {
          headers: { authorization: `Bearer ${tokens.access}` },
        });
        if (!res.ok) throw new Error(`That did not work (${res.status}).`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "stream-cut-postly.csv";
        a.click();
        URL.revokeObjectURL(url);
        note("Downloaded. Import it in Postly under Bulk Post.");
      } catch (err) {
        note(err instanceof Error ? err.message : String(err));
      }
    }
  });

  render();
}
